// Scoped CSS for SingleCruise — imported once in index.jsx

export const CRUISE_CSS = `
  .sc-page {
    background: var(--color-white);
    min-height: 100vh;
  }

  /* ── Hero ── */
  .sc-hero {
    position: relative;
    height: 90vh;
    min-height: 480px;
    display: flex;
    align-items: flex-end;
    overflow: hidden;
  }
  @media (min-width: 640px) {
    .sc-hero { height: 100vh; min-height: 600px; }
  }
  .sc-hero__bg { position: absolute; inset: 0; }
  .sc-hero__bg--zoom img {
    animation: sc-kenburns 20s ease-in-out infinite alternate;
  }
  @keyframes sc-kenburns {
    from { transform: scale(1); }
    to   { transform: scale(1.07); }
  }
  .sc-hero__bg img {
    width: 100%; height: 100%;
    object-fit: cover; display: block;
  }
  .sc-hero__bg-fallback {
    width: 100%; height: 100%;
    background: linear-gradient(135deg, var(--color-midnight) 0%, var(--color-navy) 100%);
  }
  .sc-hero__overlay {
    position: absolute; inset: 0;
    background: linear-gradient(
      to top,
      rgba(7,30,48,0.80) 0%,
      rgba(7,30,48,0.38) 40%,
      rgba(7,30,48,0.08) 75%,
      rgba(7,30,48,0)    100%
    );
  }
  .sc-hero__content {
    position: relative; z-index: 2;
    padding: 0 6vw 60px;
    width: 100%;
    max-width: 1200px;
  }
  .sc-hero__eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    font-family: var(--font-sans);
    font-size: 10px; font-weight: 500;
    letter-spacing: 0.18em; text-transform: uppercase;
    color: var(--color-gold);
    margin-bottom: 4px;
  }
  .sc-hero__title {
    font-size: clamp(2.8rem, 7vw, 5.5rem);
    color: var(--color-ivory);
    margin: 14px 0 16px;
  }
  .sc-hero__tagline {
    font-family: var(--font-serif);
    font-style: italic;
    font-size: clamp(1rem, 1.8vw, 1.25rem);
    color: rgba(245,242,236,0.6);
    margin-bottom: 28px;
  }
  .sc-hero__meta {
    display: flex; flex-wrap: wrap; gap: 20px;
    margin-bottom: 32px;
  }
  .sc-hero__meta-item {
    display: flex; align-items: center; gap: 7px;
    font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
    color: rgba(245,242,236,0.75);
  }
  .sc-hero__meta-item svg { color: var(--color-gold); }
  .sc-hero__scroll {
    position: absolute; bottom: 28px; right: 6vw; z-index: 2;
    display: flex; flex-direction: column; align-items: center; gap: 5px;
    animation: sc-bounce 2.2s ease-in-out infinite;
  }
  @keyframes sc-bounce {
    0%, 100% { transform: translateY(0); }
    50%       { transform: translateY(5px); }
  }

  /* ── Highlights bar (below hero meta, above CTA) ── */
  .sc-highlights {
    display: flex; flex-wrap: wrap; gap: 0;
    margin-bottom: 36px;
    border-top: 1px solid rgba(245,242,236,0.12);
    padding-top: 24px;
  }
  .sc-highlight-item {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 24px 8px 0;
    margin-right: 24px;
    border-right: 1px solid rgba(245,242,236,0.12);
  }
  .sc-highlight-item:last-child { border-right: none; margin-right: 0; }
  .sc-highlight-item svg { color: var(--color-gold); flex-shrink: 0; }
  .sc-highlight-item span {
    font-size: 12px; letter-spacing: 0.04em;
    color: rgba(245,242,236,0.8);
    white-space: nowrap;
  }

  /* ── Anim ── */
  .sc-anim { opacity: 0; transform: translateY(14px); transition: opacity 0.7s, transform 0.7s; }
  .sc-anim--in { opacity: 1; transform: translateY(0); }

  /* ── Cabin tab bar (inline, not sticky) ── */
  .sc-cabin-tabbar {
    margin-bottom: 32px;
    border-bottom: 1px solid var(--color-divider);
  }
  .sc-cabin-tabbar__inner {
    position: relative; display: flex; gap: 0;
  }
  .sc-cabin-tabbar__tab {
    background: none; border: none;
    color: var(--color-slate);
    padding: 14px 24px 14px 0;
    margin-right: 24px;
    cursor: pointer;
    transition: color 0.22s;
    font-size: 11px; white-space: nowrap;
  }
  .sc-cabin-tabbar__tab:hover { color: var(--color-teal); }
  .sc-cabin-tabbar__tab--active { color: var(--color-teal); }
  .sc-cabin-tabbar__indicator {
    position: absolute; bottom: 0; height: 2px;
    background: var(--color-teal);
    transition: left 0.28s cubic-bezier(0.4,0,0.2,1),
                width 0.28s cubic-bezier(0.4,0,0.2,1);
  }
  .sc-tabs__indicator {
    position: absolute; bottom: 0; height: 2px;
    background: var(--color-teal);
    transition: left 0.28s cubic-bezier(0.4,0,0.2,1),
                width 0.28s cubic-bezier(0.4,0,0.2,1);
  }

  /* ── Main wrapper ── */
  .sc-main { max-width: 1200px; margin: 0 auto; padding: 0 1.5vw; }
  .sc-section {
    padding: 30px 0;
    border-bottom: 1px solid var(--color-divider);
  }
  .sc-section:last-child { border-bottom: none; }

  /* ── Cabins ── */
  .sc-cabins { display: flex; flex-direction: column; gap: 20px; }

  .sc-cabin-card {
    background: var(--color-white);
    border: 1px solid var(--color-divider);
    overflow: hidden;
    transition: box-shadow 0.25s;
  }
  .sc-cabin-card:hover { box-shadow: 0 4px 24px rgba(11,50,80,0.10); }

  .sc-cabin-card__inner {
    display: grid;
    grid-template-columns: 380px 1fr;
  }
  @media (max-width: 860px) {
    .sc-cabin-card__inner { grid-template-columns: 1fr; }
  }

  /* Gallery slide */
  .sc-cabin-gallery {
    position: relative;
    height: 100%;
    overflow: hidden;
    background: var(--color-ivory);
    flex-shrink: 0;
  }
  .sc-cabin-gallery__img {
    width: 100%; height: 100%;
    object-fit: cover; display: block;
    transition: opacity 0.4s;
  }
  .sc-cabin-gallery__nav {
    position: absolute; inset: 0;
    display: flex; align-items: center;
    justify-content: space-between;
    padding: 0 12px;
    pointer-events: none;
  }
  .sc-cabin-gallery__btn {
    pointer-events: all;
    background: rgba(7,30,48,0.55);
    border: none; color: white;
    width: 32px; height: 32px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    transition: background 0.2s;
  }
  .sc-cabin-gallery__btn:hover { background: rgba(7,30,48,0.85); }
  .sc-cabin-gallery__dots {
    position: absolute; bottom: 10px; left: 0; right: 0;
    display: flex; justify-content: center; gap: 5px;
  }
  .sc-cabin-gallery__dot {
    width: 5px; height: 5px;
    background: rgba(255,255,255,0.45);
    border-radius: 50%;
    transition: background 0.2s;
  }
  .sc-cabin-gallery__dot--active { background: white; }
  .sc-cabin-gallery__empty {
    width: 100%; height: 100%;
    display: flex; align-items: center; justify-content: center;
    color: var(--color-slate); font-size: 12px; letter-spacing: 0.05em;
  }
  .sc-cabin-gallery__expand {
    position: absolute; bottom: 10px; right: 10px;
    display: flex; align-items: center; gap: 5px;
    background: rgba(7,30,48,0.65);
    border: 1px solid rgba(255,255,255,0.2);
    color: white;
    font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase;
    padding: 5px 10px;
    cursor: pointer;
    transition: background 0.2s;
    z-index: 2;
  }
  .sc-cabin-gallery__expand:hover { background: rgba(7,30,48,0.9); }

  /* Cabin info */
  .sc-cabin-info {
    padding: 32px 36px;
    display: flex; flex-direction: column;
  }
  .sc-cabin-info__name {
    font-family: var(--font-serif);
    font-size: 1.6rem; font-weight: 400;
    color: var(--color-teal);
    margin-bottom: 4px;
  }
  .sc-cabin-info__count {
    font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--color-gold);
    margin-bottom: 20px;
  }
  .sc-cabin-specs {
    display: grid;
    grid-template-columns: 1fr 1fr 2fr;
    gap: 16px;
    margin-bottom: 20px;
    padding: 20px 0;
    border-top: 1px solid var(--color-divider);
    border-bottom: 1px solid var(--color-divider);
  }
  .sc-cabin-spec {
    display: flex; flex-direction: column; gap: 4px;
  }
  .sc-cabin-spec__label {
    font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase;
    color: var(--color-slate);
  }
  .sc-cabin-spec__value {
    font-family: var(--font-mono);
    font-size: 1rem; color: var(--color-charcoal);
  }
  .sc-cabin-extras {
    display: flex; flex-wrap: wrap; gap: 8px;
    margin-bottom: 0;
  }
  .sc-cabin-extra-tag {
    font-size: 11px; letter-spacing: 0.06em;
    color: var(--color-slate);
    border: 1px solid var(--color-divider);
    padding: 4px 10px;
  }
  .sc-cabin-info__cta { margin-top: auto; }

  /* ── Deck Plan ── */
  .sc-deck-tabs {
    display: flex; gap: 2px;
    margin-bottom: 24px;
  }
  .sc-deck-tab {
    background: var(--color-white);
    border: 1px solid var(--color-divider);
    color: var(--color-slate);
    font-family: var(--font-sans);
    font-size: 11px; font-weight: 500;
    letter-spacing: 0.08em; text-transform: uppercase;
    padding: 10px 20px;
    cursor: pointer;
    transition: background 0.2s, color 0.2s, border-color 0.2s;
  }
  .sc-deck-tab--active {
    background: var(--color-teal);
    border-color: var(--color-teal);
    color: var(--color-ivory);
  }
  .sc-deck-plan__image {
    background: var(--color-white);
    border: 1px solid var(--color-divider);
    min-height: 400px;
    display: flex; align-items: center; justify-content: center;
    padding-top: 40px;
    padding-bottom: 40px;
  }
  .sc-deck-plan__clickable {
    position: relative; cursor: zoom-in;
    display: flex; align-items: center; justify-content: center;
    width: 100%;
  }
  .sc-deck-plan__clickable img {
    max-width: 100%; max-height: 600px;
    object-fit: contain; display: block;
    transition: opacity 0.2s;
  }
  .sc-deck-plan__clickable:hover img { opacity: 0.88; }
  .sc-deck-plan__zoom-hint {
    position: absolute; bottom: 12px; right: 12px;
    display: flex; align-items: center; gap: 6px;
    background: rgba(7,30,48,0.65); color: var(--color-ivory);
    font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase;
    padding: 5px 10px; border-radius: 2px;
    opacity: 0; transition: opacity 0.2s;
    pointer-events: none;
  }
  .sc-deck-plan__clickable:hover .sc-deck-plan__zoom-hint { opacity: 1; }
  .sc-deck-plan__placeholder {
    display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 10px;
    color: var(--color-slate); font-size: 12px; letter-spacing: 0.05em;
    padding: 60px;
    text-align: center;
  }
  .sc-deck-plan__placeholder svg { opacity: 0.25; }

  /* ── Itineraries ── */
  .sc-itins { display: flex; flex-direction: column; gap: 32px; }

  .sc-itin-group__header {
    display: flex; align-items: baseline; gap: 16px;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--color-divider);
  }
  .sc-itin-group__title {
    font-family: var(--font-mono);
    font-size: 1.1rem; font-weight: 400;
    color: var(--color-dark);
  }
  .sc-itin-group__note {
    font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--color-slate);
  }

  .sc-itin-days { display: flex; flex-direction: column; gap: 10px; }

  .sc-itin-item {
    background: var(--color-white);
    border: 1px solid var(--color-divider);
    cursor: pointer;
    transition: border-color 0.22s, box-shadow 0.22s;
  }
  .sc-itin-item:hover,
  .sc-itin-item--open {
    border-color: rgba(201,165,106,0.5);
    box-shadow: 0 2px 12px rgba(11,50,80,0.06);
  }
  .sc-itin-item__header {
    display: flex; align-items: center; gap: 20px;
    padding: 16px 20px;
  }
    .sc-itin-item--open .sc-itin-item__header{
      background-color: var(--color-teal);
    }
  .sc-itin-item__day {
    font-size: 10px; font-weight: 500; letter-spacing: 0.15em;
    text-transform: uppercase; color: var(--color-gold);
    min-width: 48px;
  }
  .sc-itin-item__title {
    font-family: var(--font-serif);
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--color-teal);
    flex: 1;
  }
      .sc-itin-item--open .sc-itin-item__title{
        color: var(--color-white);
      }
  .sc-itin-item__arrow {
    color: var(--color-slate);
    transition: transform 0.28s;
  }
  .sc-itin-item--open .sc-itin-item__arrow { transform: rotate(180deg); }
  .sc-itin-item__body {
    display: grid; grid-template-rows: 0fr;
    transition: grid-template-rows 0.32s ease, padding 0.32s ease;
    padding: 0 20px 0 88px;
  }
  .sc-itin-item__body > * { overflow: hidden; }
  .sc-itin-item--open .sc-itin-item__body {
    grid-template-rows: 1fr;
    padding: 0 20px 6px 88px;
  }
  .sc-itin-item__body p {
    font-family: var(--font-sans); font-style: normal;
    font-size: 0.95rem; color: var(--color-slate); line-height: 1.7;
    margin-top: 8px;margin-bottom: 8px;
  }

  .sc-itin-group__footer {
    margin-top: 12px;
    display: flex;
    justify-content: flex-start;
  }
  .sc-itin-group__link {
    display: inline-flex; align-items: center; gap: 7px;
    font-family: var(--font-sans);
    font-size: 11px; font-weight: 500;
    letter-spacing: 0.1em; text-transform: uppercase;
    color: var(--color-teal);
    text-decoration: none;
    padding: 10px 0;
    border-bottom: 1px solid transparent;
    transition: gap 0.22s, border-color 0.22s;
  }
  .sc-itin-group__link:hover {
    gap: 12px;
    border-bottom-color: var(--color-teal);
  }

  /* ── Gallery ── */
  .sc-gallery {
    padding: 72px 0;
    border-top: 1px solid var(--color-divider);
  }
  .sc-gallery__header {
    max-width: 1200px; margin: 0 auto;
    padding: 0 30px 32px;
  }
  /* show/hide by viewport */
  .sc-gallery--mobile { display: block; }
  .sc-gallery--grid   { display: none;  }
  @media (min-width: 640px) {
    .sc-gallery--mobile { display: none;  }
    .sc-gallery--grid   { display: block; }
  }

  /* ── Mobile carousel ── */
  .sc-gallery-carousel {
    position: relative;
    overflow: hidden;
    aspect-ratio: 4 / 3;
  }
  .sc-gallery-carousel__track {
    display: flex;
    height: 100%;
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .sc-gallery-carousel__slide {
    flex: 0 0 100%;
    height: 100%;
  }
  .sc-gallery-carousel__slide img {
    width: 100%; height: 100%;
    object-fit: cover; display: block;
  }
  .sc-gallery-carousel__btn {
    position: absolute; top: 50%; transform: translateY(-50%);
    background: rgba(7,30,48,0.45); border: none; cursor: pointer;
    color: #fff; width: 36px; height: 36px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 50%;
  }
  .sc-gallery-carousel__btn--prev { left: 10px; }
  .sc-gallery-carousel__btn--next { right: 10px; }
  .sc-gallery-carousel__counter {
    position: absolute; bottom: 10px; right: 12px;
    font-family: var(--font-sans); font-size: 11px; letter-spacing: 0.06em;
    color: #fff; background: rgba(7,30,48,0.45);
    padding: 2px 8px; border-radius: 20px;
  }
  .sc-gallery-carousel__empty {
    display: grid; grid-template-columns: repeat(2, 1fr); gap: 3px;
  }
  .sc-gallery-carousel__mock { aspect-ratio: 4/3; }

  /* ── Desktop grid ── */
  .sc-gallery__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 3px;
    max-width: 1400px;
    margin: 0 auto;
  }
  @media (min-width: 1024px) {
    .sc-gallery__grid { grid-template-columns: repeat(4, 1fr); }
  }

  /* Base cell */
  .sc-gallery__item {
    overflow: hidden;
    cursor: pointer;
    position: relative;
    grid-column: span 1;
    grid-row:    span 1;
    aspect-ratio: 4 / 3;
  }
  @media (min-width: 1024px) {
    .sc-gallery__item {
      grid-column: unset;
      grid-row:    unset;
    }
    .sc-gallery__item--large { aspect-ratio: unset; }
  }
  /* sm: 1col × 1row → 4:3 */
  .sc-gallery__item--sm    { aspect-ratio: 4 / 3; }

  .sc-gallery__item--placeholder { cursor: default; }

  .sc-gallery__item img {
    width: 100%; height: 100%;
    object-fit: cover; display: block;
    transition: transform 0.55s ease;
  }
  .sc-gallery__item:not(.sc-gallery__item--placeholder):hover img {
    transform: scale(1.05);
  }
  .sc-gallery__item:not(.sc-gallery__item--placeholder)::after {
    content: '';
    position: absolute; inset: 0;
    background: rgba(7,30,48,0);
    transition: background 0.3s;
  }
  .sc-gallery__item:not(.sc-gallery__item--placeholder):hover::after {
    background: rgba(7,30,48,0.15);
  }
  .sc-gallery__mock-label {
    width: 100%; height: 100%;
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; letter-spacing: 0.08em;
    color: rgba(11,50,80,0.35);
    font-family: var(--font-sans);
  }

  /* ── Overview ── */
  .sc-overview__top {
    display: grid;
    grid-template-columns: 1fr 140px;
    gap: 48px;
    align-items: start;
    margin-bottom: 48px;
  }
  @media (max-width: 720px) { .sc-overview__top { grid-template-columns: 1fr; } }

  .sc-overview__content { min-width: 0; }

  .sc-prose {
    font-family: var(--font-mono);
    font-size: 1.1rem; line-height: 1.5;
    color: var(--color-charcoal);
  }
  .sc-prose p + p { margin-top: 1rem; }

  /* OTA Badge */
  .sc-ota-badge {
    display: flex; flex-direction: column; align-items: center;
    gap: 8px; text-align: center;
  }
  .sc-ota-badge__svg { width: 100px; height: 116px; filter: drop-shadow(0 4px 12px rgba(0,53,128,0.25)); }
  .sc-ota-badge__label {
    font-size: 10px; font-weight: 500; letter-spacing: 0.1em;
    text-transform: uppercase; color: var(--color-slate);
  }
  .sc-ota-badge__reviews {
    font-size: 10px; color: var(--color-slate); opacity: 0.7;
  }

  /* ── Highlights grid ── */
  .sc-highlights-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 10px;
    padding-top: 20px;
  }
  @media (max-width: 900px) { .sc-highlights-grid { grid-template-columns: repeat(3, 1fr); } }
  @media (max-width: 540px) { .sc-highlights-grid { grid-template-columns: repeat(2, 1fr); } }

  .sc-highlight-box {
    display: flex; flex-direction: column;
    align-items: center; text-align: center;
    gap: 12px;
    padding: 28px 16px;
    background: var(--color-white);
    border: 1px solid var(--color-divider);
    transition: border-color 0.22s, background 0.22s, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s;
  }
  .sc-highlight-box:hover {
    border-color: var(--color-teal);
    background: var(--color-teal-mid);
    transform: translateY(-5px);
    box-shadow: 0 12px 32px rgba(10,87,118,0.14);
  }
  .sc-highlight-box__icon {
    color: var(--color-teal);
    display: flex; align-items: center; justify-content: center;
    width: 44px; height: 44px;
    transition: color 0.22s, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .sc-highlight-box:hover .sc-highlight-box__icon {
    color: var(--color-teal);
    transform: scale(1.35);
  }
  .sc-highlight-box__label {
    font-family: var(--font-sans);
    font-size: 13px; font-weight: 500;
    letter-spacing: 0.04em;
    color: var(--color-charcoal);
    line-height: 1.4;
  }

  /* ── Activities carousel ── */
  .sc-activities__header {
    display: flex; align-items: flex-start;
    justify-content: space-between; gap: 24px;
    margin-bottom: 20px;
  }
  .sc-activities__nav {
    display: flex; gap: 6px;
    padding-top: 4px;
  }
  .sc-activities__btn {
    width: 34px; height: 34px;
    border: 1px solid var(--color-divider);
    background: none;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    color: var(--color-charcoal);
    transition: background 0.2s, border-color 0.2s, color 0.2s;
  }
  .sc-activities__btn:hover:not(:disabled) {
    border-color: var(--color-teal);
    color: var(--color-teal);
  }
  .sc-activities__btn:disabled { opacity: 0.3; cursor: default; }

  .sc-activities__track-wrap {
    overflow: hidden;
  }
  .sc-activities__track {
    display: flex; gap: 10px;
    transition: transform 0.38s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: transform;
  }

  .sc-activity-card {
    flex: 0 0 calc((100% - 10px) / 2);
    min-width: 0;
    cursor: pointer;
  }
  @media (min-width: 640px) {
    .sc-activity-card { flex: 0 0 calc((100% - 20px) / 3); }
  }
  @media (min-width: 1024px) {
    .sc-activity-card { flex: 0 0 calc((100% - 30px) / 4); }
  }
  .sc-activity-card__img {
    aspect-ratio: 1/1;
    overflow: hidden;
    position: relative;
    border-radius: 4px;
  }
  .sc-activity-card__img img {
    width: 100%; height: 100%;
    object-fit: cover; display: block;
    transition: transform 0.45s ease;
  }
  .sc-activity-card:hover .sc-activity-card__img img { transform: scale(1.06); }
  .sc-activity-card__mock-icon {
    width: 100%; height: 100%;
    display: flex; align-items: center; justify-content: center;
    color: rgba(255,255,255,0.6);
  }
  .sc-activity-card__body {
    font-family: var(--font-sans);
    font-size: 12px; font-weight: 500;
    letter-spacing: 0.03em;
    color: var(--color-charcoal);
    padding: 10px 2px 0;
    line-height: 1.4;
  }
    .sc-activity-card__body span{
      font-family: var(--font-sans);
      font-size: 9px; font-weight: 500;
      letter-spacing: 0.14em; text-transform: uppercase;
      color: var(--color-gold);
    }
    .sc-activity-card__body h4{
      font-family: var(--font-serif);
      font-size: 1.25rem; font-weight: 400;
      color: var(--color-dark);
      margin: 0;
    }

  /* ── Activity Modal ── */
  .sc-act-modal__backdrop {
    position: fixed; inset: 0; z-index: 1000;
    background: rgba(7,30,48,0.72);
    display: flex; align-items: center; justify-content: center;
    padding: 24px;
  }
  .sc-act-modal {
    position: relative;
    width: 100%; max-width: 800px;
    background: var(--color-white);
    overflow: hidden;
  }
  .sc-act-modal__close {
    position: absolute; top: 12px; right: 12px; z-index: 2;
    width: 32px; height: 32px;
    background: rgba(7,30,48,0.65);
    border: none; color: white;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    transition: background 0.2s;
  }
  .sc-act-modal__close:hover { background: rgba(7,30,48,0.9); }

  .sc-act-modal__img-wrap {
    position: relative;
    aspect-ratio: 16/9;
    overflow: hidden;
  }
  .sc-act-modal__img {
    width: 100%; height: 100%;
    object-fit: cover; display: block;
  }
  .sc-act-modal__mock-icon {
    width: 100%; height: 100%;
    display: flex; align-items: center; justify-content: center;
    color: rgba(255,255,255,0.5);
  }
  .sc-act-modal__nav {
    position: absolute; top: 50%; transform: translateY(-50%);
    background: rgba(7,30,48,0.55);
    border: none; color: white;
    width: 36px; height: 36px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    transition: background 0.2s;
    z-index: 1;
  }
  .sc-act-modal__nav:hover { background: rgba(7,30,48,0.88); }
  .sc-act-modal__nav--prev { left: 10px; }
  .sc-act-modal__nav--next { right: 10px; }
  .sc-act-modal__dots {
    position: absolute; bottom: 10px; left: 0; right: 0;
    display: flex; justify-content: center; gap: 5px;
    z-index: 1;
  }
  .sc-act-modal__dot {
    width: 5px; height: 5px; border-radius: 50%;
    border: none; padding: 0;
    background: rgba(255,255,255,0.4);
    cursor: pointer;
    transition: background 0.2s;
  }
  .sc-act-modal__dot--active { background: white; }
  .sc-act-modal__counter {
    position: absolute; bottom: 10px; right: 12px;
    font-size: 10px; letter-spacing: 0.08em;
    color: rgba(255,255,255,0.7);
    z-index: 1;
  }

  .sc-act-modal__body {
    padding: 24px 28px 28px;
    display: flex; flex-direction: column; gap: 8px;
  }
  .sc-act-modal__tag {
    font-family: var(--font-sans);
    font-size: 9px; font-weight: 500;
    letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--color-gold);
  }
  .sc-act-modal__title {
    font-family: var(--font-serif);
    font-size: 1.55rem; font-weight: 400;
    color: var(--color-dark);
    margin: 0;
  }
  .sc-act-modal__desc {
    font-family: var(--font-sans);
    font-size: 14px; line-height: 1.7;
    color: var(--color-slate);
    margin: 4px 0 0;
  }

  /* ── Layout blocks ── */
  .sc-block { border-top: 1px solid var(--color-divider); }

  .sc-cabin-block {
    max-width: 1200px;
    margin: 0 auto;
    padding: 30px 1.5vw;
    border-top: 1px solid var(--color-divider);
  }
  .sc-cabin-block__heading { margin-bottom: 0; }
`
